'use client';

import { FC, useState, useEffect } from 'react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, LockOpen, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const API_URL = process.env.NEXT_PUBLIC_API_URL;

interface RankingData {
  teamId?: number;
  leagueId?: number;
  teamName?: string;
  leagueName?: string;
  rank: number;
  points?: number;
  score?: number;
}

interface RankingResponse {
  data: RankingData[];
  totalRecords: number;
}

const RECORDS_PER_PAGE = 10;

export default function Ranking() {
  const { competitionData, competitionConfig, brand } = useCompetitionContext();
  
  // State
  const [activeTab, setActiveTab] = useState<'general' | 'week' | 'leagues' | 'leaguesWeek'>('general');
  const [rankings, setRankings] = useState<RankingData[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchName, setSearchName] = useState<string>('');
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(Math.max(competitionData?.deadlineInfo?.deadlineWeek-1, 1));
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  
  // Pagination calculations
  const totalPages = Math.ceil(totalRecords / RECORDS_PER_PAGE);
  
  const tabs = [
    { name: 'Algemeen', key: 'general', current: activeTab === 'general' },
    { name: 'Race', key: 'week', current: activeTab === 'week' },
    { name: 'Vriendenleagues', key: 'leagues', current: activeTab === 'leagues' },
    { name: 'Vriendenleagues Race', key: 'leaguesWeek', current: activeTab === 'leaguesWeek' },
  ];

  // Function to fetch rankings data
  const fetchRankings = async () => {
    setIsLoading(true);
    
    try {
      let url = '';
      
      if (activeTab === 'general' || activeTab === 'leagues') {
        url = `${API_URL}/redis/${activeTab}?pageRecords=${RECORDS_PER_PAGE}&pageNumber=${pageNumber}&name=${searchName}&competitionFeed=${competitionConfig.competitionFeed}&seasonId=${competitionConfig.seasonId}`;
        
        // Add title filter for general tab
        if (activeTab === 'general' && selectedTitle) {
          url += `&title=${selectedTitle}`;
        }
      } else {
        // Default to week 1 if no week is selected
        const weekId = selectedWeekId || 1;
        url = `${API_URL}/redis/${activeTab}?pageRecords=${RECORDS_PER_PAGE}&pageNumber=${pageNumber}&name=${searchName}&weekId=${weekId}&competitionFeed=${competitionConfig.competitionFeed}&seasonId=${competitionConfig.seasonId}`;
        
        // Add title filter for week tab
        if (activeTab === 'week' && selectedTitle) {
          url += `&title=${selectedTitle}`;
        }
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch rankings: ${response.statusText}`);
      }
      
      const data: RankingResponse = await response.json();
      setRankings(data.data);
      setTotalRecords(data.totalRecords);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch rankings when relevant state changes
  useEffect(() => {
    fetchRankings();
  }, [activeTab, pageNumber, selectedWeekId, selectedTitle, competitionConfig.competitionFeed, competitionConfig.seasonId]);
  
  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPageNumber(1); // Reset to first page on new search
    fetchRankings();
  };
  
  function classNames(...classes: any) {
    return classes.filter(Boolean).join(' ');
  }

  return (
    <div className="bg-white p-4 pb-8">
      <h1 className="text-xl font-bold my-4">Klassement</h1>
      
      {/* Mobile Tab Selector */}
      <div className="grid grid-cols-1 sm:hidden mb-4">
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value as 'general' | 'week')}
          aria-label="Select a tab"
          className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
        >
          {tabs.map((tab) => (
            <option key={tab.key} value={tab.key}>{tab.name}</option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
        />
      </div>
      
      {/* Desktop Tabs */}
      <div className="hidden sm:block">
        <div className="border-b border-gray-200">
          <nav aria-label="Tabs" className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as 'general' | 'week')}
                aria-current={tab.current ? 'page' : undefined}
                className={classNames(
                  tab.current
                    ? `border-${brand}-primary text-${brand}-primary`
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium'
                )}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Filters */}
      <div className="mt-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Title Selection (shown for general and week tabs) */}
          {(activeTab === 'general' || activeTab === 'week') && (
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">Titel</label>
              <Select
                onValueChange={(value) =>
                  value === "all" ? setSelectedTitle(null) : setSelectedTitle(value)
                }
                value={selectedTitle !== null ? selectedTitle : "all"}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecteer een title" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="all" value="all">
                    Algemeen klassement
                  </SelectItem>
                  {[
                    {key: 'ad', title: "het AD"},
                    {key: 'bd', title: "Brabants Dagblad"},
                    {key: 'bs', title: "BN DeStem"},
                    {key: 'dg', title: "De Gelderlander"}, 
                    {key: 'ds', title: "De Stentor"},
                    {key: 'ed', title: "Eindhovens Dagblad"}, 
                    {key: 'nu', title: "NU.nl"},
                    {key: 'pzc', title: "PZC"},
                    {key: 'tt', title: "Tubantia"}
                  ].map((title) => (
                    <SelectItem key={title.key} value={title.key}>
                      {title.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Week Selection (only shown for week tab) */}
          {(activeTab === 'week' || activeTab === 'leaguesWeek') && (
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">Race</label>
              <Select
                onValueChange={(value) =>
                  value === "unset" ? setSelectedWeekId(null) : setSelectedWeekId(Number(value))
                }
                value={selectedWeekId !== null ? String(selectedWeekId) : "unset"}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Kies een race" />
                </SelectTrigger>
                <SelectContent>
                  {/* Option to unset the week */}
                  <SelectItem key="unset" value="unset">
                    Geen race geselecteerd
                  </SelectItem>
                  {competitionData.weeks.map((week: any) => (
                    <SelectItem key={week.weekId} value={String(week.weekId)}>
                      {week.weekId}.{" "}
                      {competitionData.matches.find(
                        (match: any) => match.weekId === week.weekId
                      )?.feedUrl}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* 
          <div className="w-full">
            <form onSubmit={handleSearch} className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Team Naam</label>
                <Input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Zoek op teamnaam"
                  className="w-full"
                />
              </div>
              <Button type="submit" className={`bg-${brand}-primary hover:bg-${brand}-primary/90`}>
                Zoeken
              </Button>
            </form>
          </div> */}
        </div>
      </div>
      
      {/* Rankings Table */}
      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Rank</TableHead>
              <TableHead>{(activeTab === 'leagues' || activeTab === 'leaguesWeek') ? `Vriendenleague` : `Team`}</TableHead>
              <TableHead className="text-right">Punten</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : rankings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">
                  Geen resultaten gevonden
                </TableCell>
              </TableRow>
            ) : (
              rankings.map((data, index) => {
                return (activeTab === 'leagues' || activeTab === 'leaguesWeek') ?
                  (
                    // For leagues, use leagueId with fallback to index for unique key
                    <TableRow key={`league_${data.leagueId || index}`}>
                      <TableCell className="font-medium">{data.rank}</TableCell>
                      <TableCell>
                        {data.leagueName}
                      </TableCell>
                      <TableCell className="text-right">{data.score?.toFixed(2)}</TableCell>
                    </TableRow>
                  ) : (
                    // For teams, use teamId with fallback to index for unique key
                    <TableRow key={`team_${data.teamId || index}`}>
                      <TableCell className="font-medium">{data.rank}</TableCell>
                      <TableCell>
                        <a href={`/fantasy/public/${data.teamId}`} className={`text-${brand}-primary hover:underline`}>
                          {data.teamName}
                        </a>
                      </TableCell>
                      <TableCell className="text-right">{data.points}</TableCell>
                    </TableRow>
                  );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination */}
      <div className="flex justify-end items-center p-2 gap-2">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              onClick={() => setPageNumber(p => Math.max(1, p - 1))}
              disabled={pageNumber === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
              disabled={pageNumber === totalPages}
              className="relative ml-30 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pageNumber - 1) * RECORDS_PER_PAGE + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pageNumber * RECORDS_PER_PAGE, totalRecords)}
                </span>{' '}
                of <span className="font-medium">{totalRecords}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  disabled={pageNumber === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                  // Create a window of pages around the current page
                  let pageToShow: number;
                  if (totalPages <= 5) {
                    pageToShow = i + 1;
                  } else if (pageNumber <= 3) {
                    pageToShow = i + 1;
                  } else if (pageNumber >= totalPages - 2) {
                    pageToShow = totalPages - 4 + i;
                  } else {
                    pageToShow = pageNumber - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageToShow}
                      onClick={() => setPageNumber(pageToShow)}
                      aria-current={pageNumber === pageToShow ? 'page' : undefined}
                      className={classNames(
                        pageNumber === pageToShow
                          ? `relative z-10 inline-flex items-center bg-${brand}-primary px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`
                          : `relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0`
                      )}
                    >
                      {pageToShow}
                    </Button>
                  );
                })}
                
                <Button
                  onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                  disabled={pageNumber === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </Button>
              </nav>
            </div>
          </div>
        </div>
    </div>
  );
}